"use client"

import { type FragmentOf, readFragment } from "@opensea/graphql"
import { Avatar, ItemAvatarBadge } from "@opensea/next-ui-kit"
import { type AvatarBadgeProps, classNames, Tooltip } from "@opensea/ui-kit"
import { useTranslations } from "next-intl"
import { useChains } from "../../hooks/use-chains"
import { ChainBadgeFragment } from "./ChainBadge.fragment"

type ChainBadgeFragmentProps = {
  chain: FragmentOf<typeof ChainBadgeFragment>
}

type ChainBadgeIdentifierProps = {
  identifier: string
}

type Props = (ChainBadgeFragmentProps | ChainBadgeIdentifierProps) & {
  size?: AvatarBadgeProps["size"]
  className?: string
  shape?: "circle" | "square"
  color?: "colored" | "monochrome"
}

export function ChainBadge({
  size = "md",
  shape = "square",
  color = "colored",
  className,
  ...rest
}: Props) {
  const { identifier } =
    "identifier" in rest ? rest : readFragment(ChainBadgeFragment, rest.chain)

  const { getLogo, getChainName } = useChains()
  const t = useTranslations("ChainBadge")

  const logo = getLogo(identifier, {
    circle: shape === "circle",
    square: shape === "square" && color === "colored",
    monochrome: color === "monochrome",
  })

  const name = getChainName(identifier)

  return (
    <Tooltip content={t("tooltip", { name })}>
      <ItemAvatarBadge
        className={classNames(
          "bg-bg-additional-2",
          color === "colored" && "p-0",
          className,
        )}
        size={size}
      >
        <Avatar
          {...logo}
          title={name}
          {...("icon" in logo && {
            overrides: { Icon: { size: "100%" } },
          })}
          className={classNames(
            "text-text-primary",
            "src" in logo && "size-full",
          )}
        />
      </ItemAvatarBadge>
    </Tooltip>
  )
}
